import { makeObservable, observable, action, runInAction } from "mobx";
import { mode } from "@core/interfaces/mode";
import {
    devicesAPI,
    IDevice,
    IAddDeviceToDbReq,
    IAddDeviceReq,
    IAddOldDeviceReq,
    IDeviceSettingsCommand,
    DeviceSettingsCommandStatus,
} from "@api/Devices.api";
import { IChangeDevice } from "./Groups.store";
import { groupsAPI, NO_GROUP_ID } from "@api/Groups.api";
import getDisplayedDeviceSettings from "@utils/getDisplayedDeviceSettings";
import Settings from "@core/Settings";
import getDemoDate from "@utils/getDemoDate";
import { RootStore } from "./Root.store";
import { ICoords, ITelemetry, IRawTelemetry } from "@core/Telemetry";
import { ISettings } from "@core/interfaces/ISettings";
import { IRawSettings } from "@core/interfaces/IRawSettings";

type IBaseParams = Omit<IDevice, "last_coord" | "telemetry">;
type IRawTelemetryParams = Pick<
    IRawTelemetry,
    "speed" | "dir" | "alt" | "sats" | "hdop"
>;
type ITelemetryParams = Pick<
    ITelemetry,
    "event" | "temperature" | "cur_sim" | "signal"
>;

export interface IStoredDevice
    extends IBaseParams,
        IRawTelemetryParams,
        ITelemetryParams {
    groupId: string;
    gpsCoord: ICoords;
    lbsCoord: ICoords;
    address: string;
    date: Date | number | null;
    gpsDate: Date | number | null;
    lbsDate: Date | number | null;
    batteryVoltage: number;
}

export interface IStatus {
    error?: boolean;
    reason?: string;
}

export interface IStroredSettings extends Partial<Settings> {
    imei: string;
    status: IStatus;
    command?: IDeviceSettingsCommand | null;
}

interface IAddDevice
    extends Omit<IAddDeviceReq, "token" | "icon">,
        Partial<Pick<IAddDeviceReq, "icon">> {}

interface IAddOldDevice extends Omit<IAddOldDeviceReq, "token"> {}

interface IAddDeviceAndGroupReq
    extends Omit<IAddDeviceReq, "token" | "groupId"> {
    groupName: string;
    userIcon?: number;
}

export default class DevicesStore {
    @observable
    public defaultIcon = 1;

    @observable
    public modes: mode[] = ["tracker", "periodic", "alarm"];

    @observable
    public supportList: string[];

    @observable
    public deviceList: IStoredDevice[];

    @observable
    public settingsList: IStroredSettings[];

    @observable
    public lastCoordsList: IStoredDevice[];

    @observable
    public serverError: boolean;

    @observable
    public loading: boolean;

    @observable
    public addDeviceLoading: boolean;

    @observable
    public imeiNotExistError: boolean;

    @observable
    public deviceAlreadyExistError: boolean;

    @observable
    public deviceAlreadyInUseError: boolean;

    @observable
    public noRightsError: boolean;

    @observable
    public operationSuccess: boolean;

    @observable
    public selectedDeviceImei: string | null;

    @observable
    public editedDeviceImei: string | null;

    @observable
    public infoDeviceImei: string | null;

    @observable
    public settingDeviceImei: string | null;

    @observable
    public selectedDeviceSettings: Partial<IStroredSettings>;

    @observable
    public selectedDeviceApplied: boolean;

    @observable
    public selectedDeviceCommandStatus: DeviceSettingsCommandStatus | null;

    @observable
    public selectedDeviceCommandFailureReason: string | null;

    @observable
    public selectedDeviceSettingsTs: number | null;

    @observable
    public selectedDeviceCommandCreatedAt: string | null;

    @observable
    public infoDeviceSettings: Partial<IStroredSettings>;

    @observable
    public sharedSettings: ISettings | null;

    @observable
    public errorDevices: string[];

    @observable
    public settingsLoading: boolean;

    @observable
    public addDeviceInDbLoading: boolean;

    @observable
    public changeDeviceDescriptionLoading: boolean;

    @observable
    public deleteDeviceLoading: boolean;

    @observable
    public wrongPasswordError: boolean;

    @observable
    public operationSuccessOldDevices: boolean;

    @observable
    public selectedDeviceStatus: IStatus;

    @observable
    public deviceOldList: IStoredDevice[];

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.supportList = [];
        this.deviceList = [];
        this.deviceOldList = [];
        this.lastCoordsList = [];
        this.serverError = false;
        this.imeiNotExistError = false;
        this.deviceAlreadyExistError = false;
        this.deviceAlreadyInUseError = false;
        this.noRightsError = false;
        this.operationSuccess = false;
        this.selectedDeviceImei = null;
        this.settingDeviceImei = null;
        this.editedDeviceImei = null;
        this.infoDeviceImei = null;
        this.loading = false;
        this.addDeviceLoading = false;
        this.settingsList = [];
        this.selectedDeviceSettings = {};
        this.selectedDeviceStatus = {};
        this.selectedDeviceApplied = false;
        this.selectedDeviceCommandStatus = null;
        this.selectedDeviceCommandFailureReason = null;
        this.selectedDeviceSettingsTs = null;
        this.selectedDeviceCommandCreatedAt = null;
        this.infoDeviceSettings = {};
        this.sharedSettings = null;
        this.errorDevices = [];
        this.settingsLoading = false;
        this.addDeviceInDbLoading = false;
        this.changeDeviceDescriptionLoading = false;
        this.deleteDeviceLoading = false;
        this.wrongPasswordError = false;
        this.operationSuccessOldDevices = false;
    }

    @action
    async getAllDevices() {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            const res = await devicesAPI.getAllDevices({
                token,
            });
            if (res.result === "success") {
                runInAction(() => {
                    this.supportList = res.devices.map(({ imei }) => imei);
                });
            } else {
                runInAction(() => {
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.serverError = true;
            });
        }
    }

    @action
    async getGroupDevices(groupId: string) {
        const token = this.rootStore.user.token;

        if (!token) return;
        try {
            const limit = await this.rootStore.groups.getLength(groupId);
            if (!limit) {
                this.deviceList = this.deviceList.filter(
                    (device) => device.groupId !== groupId
                );
                return;
            }
            const res = await devicesAPI.getGroupDevices({
                token,
                groupId,
                limit,
                lastCoord: true,
                telemetry: true,
                deviceType: true,
            });
            // //////ПОСЛЕ ПЕРВИЧНОЙ АВТОРИЗАЦИИ ЗДЕСЬ БУДЕТ ПРОИСХОДИТ ДОБАВЛЕНИЕ СТАРЫХ УСТРОЙСТВ ПРИ ЗА ГРУЗКЕ КАБИНЕТА
            // if (res.activate) {
            //     this.rootStore.user.loginOldActivated = true;
            // }
            if (res.result === "success") {
                runInAction(() => {
                    const getCurSim = (code: number) => {
                        switch (code) {
                            case 1:
                                return "SIM 1";
                            case 2:
                                return "SIM 2";
                            default:
                                return "";
                        }
                    };
                    const resDevices = res.devices;
                    const resImei = resDevices.map((device) => device.imei);
                    const devices = this.deviceList
                        .filter((device) => device.groupId !== groupId)
                        .filter(
                            (device) => resImei.indexOf(device.imei) === -1
                        );
                    const devicesToAdd = resDevices.map(
                        ({
                            imei,
                            name,
                            type,
                            additionalInfo,
                            deviceType,
                            icon,
                            telemetry,
                            last_coord,
                            address,
                        }) => {
                            const {
                                ts_s,
                                speed,
                                dir,
                                alt,
                                sats,
                                hdop,
                                bat_v: batteryVoltage,
                                event,
                                temp,
                                cur_sim,
                                signal,
                            } = telemetry;
                            const {
                                lat,
                                lon,
                                ts_gps,
                                lat_lbs,
                                lon_lbs,
                                ts_lbs,
                            } = last_coord;
                            const gpsCoord = { lat, lon };
                            const lbsCoord = { lat: lat_lbs, lon: lon_lbs };
                            let gpsDate: Date | number | null;
                            let lbsDate: Date | number | null;
                            let date: Date | number | null;
                            if (this.rootStore.app.demoMode) {
                                date = getDemoDate(ts_s);
                                gpsDate = getDemoDate(ts_gps);
                                lbsDate = getDemoDate(ts_lbs);
                            } else {
                                date = ts_s ? new Date(ts_s) : null;
                                gpsDate = ts_gps ? new Date(ts_gps) : null;
                                lbsDate = ts_lbs ? new Date(ts_lbs) : null;
                            }
                            const actualAddress = address || last_coord.address

                            return {
                                imei,
                                deviceType,
                                name,
                                icon,
                                type,
                                additionalInfo,
                                date,
                                gpsCoord,
                                lbsCoord,
                                address: actualAddress,
                                gpsDate,
                                lbsDate,
                                speed,
                                dir,
                                alt,
                                sats,
                                hdop,
                                batteryVoltage,
                                groupId,
                                event:
                                    typeof event === "string"
                                        ? event.toLowerCase()
                                        : "",
                                temperature: temp,
                                cur_sim: getCurSim(cur_sim),
                                signal,
                            };
                        }
                    );
                    this.deviceList = [...devices, ...devicesToAdd] as IStoredDevice[];
                });
            } else {
                runInAction(() => {
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.serverError = true;
            });
        }
    }

    @action
    async getGroupsDevices() {
        if (!this.loading) {
            this.loading = true;
            await Promise.all(
                this.rootStore.groups.groupsList.map(async (group) => {
                    await this.getGroupDevices(group.id);
                })
            );
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    async getDeviceSettings(imei: string) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.settingsLoading = true;
            const res = await devicesAPI.getDeviceSettings(token, imei);
            if (res.result === "success") {
                const _settings = res.settings;
                const command = res.command ?? null;
                const keysLength = Object.keys({ ..._settings }).length;
                if (keysLength > 0 && !(keysLength === 1 && !_settings.ts))
                    runInAction(() => {
                        const settings = _settings;
                        const status = {};
                        const result = { imei, settings, status, command };
                        const devices = this.settingsList.filter(
                            (item) => item.imei !== imei
                        );
                        this.settingsLoading = false;
                        this.settingsList = [...devices, result];
                    });
                else
                    runInAction(() => {
                        this.settingsLoading = false;
                        const settings = {};
                        const status = {
                            error: true,
                            reason: "noSettingsError",
                        };
                        const result = { imei, settings, status, command };
                        const devices = this.settingsList.filter(
                            (item) => item.imei !== imei
                        );
                        this.settingsLoading = false;
                        this.settingsList = [...devices, result];
                    });
            } else if (
                res.result === "error" &&
                res.info.indexOf("no_rights") >= 0
            ) {
                runInAction(() => {
                    this.settingsLoading = false;
                    const settings = {};
                    const status = {
                        error: true,
                        reason: "noRightsDeviceSettingsError",
                    };
                    const result = { imei, settings, status };
                    const devices = this.settingsList.filter(
                        (item) => item.imei !== imei
                    );
                    this.settingsLoading = false;
                    this.settingsList = [...devices, result];
                });
            } else {
                runInAction(() => {
                    this.settingsLoading = false;
                    const settings = {};
                    const status = {
                        error: true,
                        reason: "serverError",
                    };
                    const result = { imei, settings, status };
                    const devices = this.settingsList.filter(
                        (item) => item.imei !== imei
                    );
                    this.settingsLoading = false;
                    this.settingsList = [...devices, result];
                });
            }
        } catch (e) {
            runInAction(() => {
                this.settingsLoading = false;
                this.serverError = true;
            });
        }
    }

    preventOperationSuccess = false;
    setPreventoperationSuccess() {
        this.preventOperationSuccess = true;
    }
    @action
    async sendDeviceSettings(settings: ISettings) {
        const token = this.rootStore.user.token;
        const imei = this.settingDeviceImei;
        if (!token || !imei) return;
        try {
            this.settingsLoading = true;
            const result = Settings.encodeSettings(settings);
            const res = await devicesAPI.changeDeviceSettings(
                token,
                imei,
                result
            );
            if (res.result === "success") {
                runInAction(() => {
                    this.settingsLoading = false;
                    if (!this.preventOperationSuccess) {
                        this.operationSuccess = true;
                    } else {
                        this.preventOperationSuccess = false;
                    }
                });
            } else {
                runInAction(() => {
                    this.settingsLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.settingsLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async _sendDeviceSettings(settings: IRawSettings, imei: string) {
        const token = this.rootStore.user.token;
        if (!token) return;
        const res = await devicesAPI.changeDeviceSettings(
            token,
            imei,
            settings
        );
        if (res.result == "success") {
            runInAction(() => {
                this.getDeviceSettings(imei);
            });
        } else {
            throw new Error("Server error");
        }
    }

    @action
    async shareDeviceSettings(devices: IStoredDevice[]) {
        this.loading = true;
        const sharedSettings = this.sharedSettings;
        if (!sharedSettings) return;
        const settings = Settings.encodeSettings(sharedSettings);
        try {
            await Promise.all(
                devices.map(async ({ imei }) => {
                    await this._sendDeviceSettings(settings, imei);
                })
            );
            runInAction(() => {
                this.loading = false;
                this.operationSuccess = true;
            });
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async getDevicesSettings() {
        // Лагает
        // this.deviceList.map((device) => {
        //     this.getDeviceSettings(device.imei);
        // });
    }

    @action
    async addDeviceInDb(args: Omit<IAddDeviceToDbReq, "token">) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.addDeviceInDbLoading = true;
            const res = await devicesAPI.addDeviceInDb({ token, ...args });
            if (res.result === "success") {
                runInAction(() => {
                    this.addDeviceInDbLoading = false;
                    this.operationSuccess = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("already_exists") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceInDbLoading = false;
                    this.deviceAlreadyExistError = true;
                });
            } else {
                runInAction(() => {
                    this.addDeviceInDbLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.addDeviceInDbLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async addDevice({
        imei,
        name,
        icon,
        groupId,
        password,
        type,
        additionalInfo,
    }: IAddDevice) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.addDeviceLoading = true;
            const args = {
                token,
                imei,
                name,
                groupId,
                password,
                icon: icon ?? 1,
                type,
                additionalInfo,
            };
            const res = await devicesAPI.addDevice(args);
            if (res.result === "success") {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.operationSuccess = true;
                });
                await this.getGroupDevices(groupId ?? NO_GROUP_ID);
            } else if (
                res.result === "error" &&
                res.info.indexOf("not_exists_device") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.imeiNotExistError = true;
                    this.errorDevices = [...this.errorDevices, imei];
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("already_exists") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.deviceAlreadyExistError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("already_in_use") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.deviceAlreadyInUseError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("no_rights") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.noRightsError = true;
                    this.errorDevices = [...this.errorDevices, imei];
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("wrong_password") >= 0
            ) {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.wrongPasswordError = true;
                    this.errorDevices = [...this.errorDevices, imei];
                });
            } else {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.addDeviceLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async addDeviceAndGroup({
        imei,
        name,
        icon,
        groupName,
        password,
        userIcon,
        type,
        additionalInfo,
    }: IAddDeviceAndGroupReq) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.loading = true;
            const groupIco =
                userIcon !== undefined
                    ? userIcon
                    : this.rootStore.groups.defaultIcon;
            const createGroupRes = await groupsAPI.createGroup(
                token,
                groupName,
                groupIco
            );
            if (createGroupRes.result !== "success") {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
                return;
            }
            const groupsRes = await groupsAPI.getUserGroups(token);
            if (groupsRes.result !== "success") {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
                return;
            }
            const [{ id: groupId }] = groupsRes.groups.filter(
                (group) => group.name === groupName
            );
            const args: any = {
                token,
                imei,
                name,
                groupId,
                password,
                type,
                additionalInfo,
            };
            args.icon = icon ?? 1;
            const res = await devicesAPI.addDevice(args);
            if (res.result === "success") {
                runInAction(() => {
                    this.loading = false;
                    this.operationSuccess = true;
                    this.rootStore.groups.getUserGroups();
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("not_exists_device") >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.imeiNotExistError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("already_exists") >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.deviceAlreadyExistError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("already_in_use") >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.deviceAlreadyInUseError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("no_rights") >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.noRightsError = true;
                });
            } else if (
                res.result === "error" &&
                res.info.indexOf("wrong_password") >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.wrongPasswordError = true;
                });
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    getGroupLastCoords(groupId: string | null) {
        if (groupId) {
            this.lastCoordsList = this.deviceList
                .filter((device) => device.groupId === groupId)
                .filter(({ gpsCoord, lbsCoord }) => {
                    const { lat, lon } = gpsCoord;
                    const { lat: lbsLat, lon: lbsLon } = lbsCoord;

                    return (lat && lon) || (lbsLat && lbsLon);
                });
        } else this.lastCoordsList = [];
    }

    @action
    async getDeviceLastCoords(imei: string) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            const res = await devicesAPI.getOneDevice({
                token,
                imei,
                lastCoord: true,
                telemetry: true,
                deviceType: true,
            });
            if (res.result === "success") {
                runInAction(() => {
                    const getCurSim = (code: number) => {
                        switch (code) {
                            case 1:
                                return "SIM 1";
                            case 2:
                                return "SIM 2";
                            default:
                                return "";
                        }
                    };
                    const resDevices = res.devices;
                    const devicesToAdd = resDevices.map(
                        ({
                            imei,
                            name,
                            type,
                            deviceType,
                            additionalInfo,
                            address,
                            icon,
                            telemetry,
                            last_coord,
                        }) => {
                            const {
                                ts_s,
                                speed,
                                dir,
                                alt,
                                sats,
                                hdop,
                                bat_v: batteryVoltage,
                                event,
                                temp,
                                cur_sim,
                                signal,
                            } = telemetry;
                            const {
                                lat,
                                lon,
                                ts_gps,
                                lat_lbs,
                                lon_lbs,
                                ts_lbs,
                            } = last_coord;
                            const gpsCoord = { lat, lon };
                            const lbsCoord = { lat: lat_lbs, lon: lon_lbs };
                            let gpsDate: Date | number | null;
                            let lbsDate: Date | number | null;
                            let date: Date | number | null;
                            if (this.rootStore.app.demoMode) {
                                date = getDemoDate(ts_s);
                                gpsDate = getDemoDate(ts_gps);
                                lbsDate = getDemoDate(ts_lbs);
                            } else {
                                date = ts_s ? new Date(ts_s) : null;
                                gpsDate = ts_gps ? new Date(ts_gps) : null;
                                lbsDate = ts_lbs ? new Date(ts_lbs) : null;
                            }
                            return {
                                imei,
                                name,
                                icon,
                                type,
                                deviceType,
                                additionalInfo,
                                date,
                                gpsCoord,
                                lbsCoord,
                                gpsDate,
                                lbsDate,
                                address,
                                speed,
                                dir,
                                alt,
                                sats,
                                hdop,
                                batteryVoltage,
                                groupId: "",
                                event:
                                    typeof event === "string"
                                        ? event.toLowerCase()
                                        : "",
                                temperature: temp,
                                cur_sim: getCurSim(cur_sim),
                                signal,
                            };
                        }
                    );
                    this.lastCoordsList = devicesToAdd;
                });
            } else {
                runInAction(() => {
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.serverError = true;
            });
        }
    }

    @action
    async changeDeviceDescription({
        name,
        icon: userIcon,
        type,
        additionalInfo,
    }: IChangeDevice) {
        const token = this.rootStore.user.token;
        const imei = this.editedDeviceImei;
        if (!imei || !token) return;
        try {
            this.changeDeviceDescriptionLoading = true;
            const icon = userIcon !== undefined ? userIcon : this.defaultIcon;
            const description: IChangeDevice = { name, icon };
            if (type) description.type = type;
            if (additionalInfo) description.additionalInfo = additionalInfo;
            const res = await devicesAPI.changeDeviceDescription(
                token,
                imei,
                description
            );
            if (res.result === "success") {
                runInAction(() => {
                    this.changeDeviceDescriptionLoading = false;
                    this.operationSuccess = true;
                    this.getGroupsDevices();
                });
            } else {
                runInAction(() => {
                    this.changeDeviceDescriptionLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.changeDeviceDescriptionLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async deleteDevice() {
        const token = this.rootStore.user.token;
        const imei = this.selectedDeviceImei;
        if (!imei || !token) return;
        try {
            this.deleteDeviceLoading = true;
            const [device] = this.deviceList.filter(
                (item) => item.imei === imei
            );
            const res = await devicesAPI.deleteDevice(token, imei);
            if (res.result === "success") {
                runInAction(() => {
                    this.deleteDeviceLoading = false;
                    this.operationSuccess = true;
                    this.rootStore.map.removeSelectedDevices(imei);
                    this.getGroupDevices(device.groupId);
                });
            } else {
                runInAction(() => {
                    this.deleteDeviceLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.deleteDeviceLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    setSharedSettings(settings: ISettings) {
        this.sharedSettings = settings;
    }

    @action
    resetSharedSettings() {
        this.sharedSettings = null;
    }

    @action
    setSelectedDeviceImei(imei: string) {
        this.selectedDeviceImei = imei;
    }

    @action
    resetSelectedDeviceImei() {
        this.selectedDeviceImei = null;
    }

    @action
    setSettingDeviceImei(imei: string) {
        this.settingDeviceImei = imei;
    }

    @action
    resetSettingDeviceImei() {
        this.settingDeviceImei = null;
    }

    @action
    setEditedDeviceImei(imei: string) {
        this.editedDeviceImei = imei;
    }

    @action
    resetEditedDeviceImei() {
        this.editedDeviceImei = null;
    }

    @action
    setInfoDeviceImei(imei: string) {
        this.infoDeviceImei = imei;
    }

    @action
    resetInfoDeviceImei() {
        this.infoDeviceImei = null;
    }

    @action
    setSelectedDeviceSettings(imei: string) {
        if (imei) {
            const [{ settings, status, command }] = this.settingsList.filter(
                (item) => item.imei === imei
            );
            const displayedSettings = getDisplayedDeviceSettings(
                settings,
                command,
            );
            this.selectedDeviceSettings = new Settings(displayedSettings);
            this.selectedDeviceStatus = status;
            this.selectedDeviceCommandStatus = command?.status ?? null;
            this.selectedDeviceCommandFailureReason =
                command?.failure_reason ?? null;
            this.selectedDeviceApplied = command
                ? command.status === "COMPLETED"
                : settings?.applied !== false;
            this.selectedDeviceSettingsTs = settings?.ts ?? null;
            this.selectedDeviceCommandCreatedAt = command?.created_at ?? null;
        } else {
            this.selectedDeviceSettings = {};
            this.selectedDeviceStatus = {};
            this.selectedDeviceApplied = false;
            this.selectedDeviceCommandStatus = null;
            this.selectedDeviceCommandFailureReason = null;
            this.selectedDeviceSettingsTs = null;
            this.selectedDeviceCommandCreatedAt = null;
        }
    }

    @action
    setInfoDeviceSettings(imei: string) {
        if (imei) {
            this.getDeviceSettings(imei).then(
                action(() => {
                    const [{ settings }] = this.settingsList.filter(
                        (item) => item.imei === imei
                    );
                    if (settings && Object.keys(settings).length) {
                        this.infoDeviceSettings = new Settings({ ...settings });
                    } else {
                        this.infoDeviceSettings = {};
                    }
                })
            );
        } else this.infoDeviceSettings = {};
    }

    @action
    resetOperationSuccess() {
        this.operationSuccess = false;
    }

    @action
    resetServerError() {
        this.serverError = false;
    }

    @action
    resetImeiNotExistError() {
        this.imeiNotExistError = false;
    }

    @action
    resetDeviceAlreadyExistError() {
        this.deviceAlreadyExistError = false;
    }

    @action
    resetDeviceAlreadyInUseError() {
        this.deviceAlreadyInUseError = false;
    }

    @action
    resetNoRightsError() {
        this.noRightsError = false;
    }

    @action
    resetWrongPasswordError() {
        this.wrongPasswordError = false;
    }

    @action
    resetErrorDevice() {
        this.errorDevices = [];
    }
}
